import { motion } from 'framer-motion';
import React from 'react';

interface HeroProps {
  onGetStartedClick: () => void;
}

export default function Hero({ onGetStartedClick }: HeroProps) {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleGetStartedClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onGetStartedClick();
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-sky-400 via-blue-300 to-cyan-200 pt-24 px-4">
      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-white/20"></div>
      
      {/* Decorative elements */}
      <motion.div
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-32 right-20 w-24 h-24 bg-yellow-300 rounded-full opacity-60 blur-xl"
      ></motion.div>
      <motion.div
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-40 left-20 w-32 h-32 bg-red-400 rounded-full opacity-40 blur-xl"
      ></motion.div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center w-full">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-1"
        >
          <img 
            src="https://static.readdy.ai/image/631821f386b34ef1da569fb8b8560804/250342c89f5a32ed1fef7abf73de2d64.png" 
            alt="Can-do Crew Logo" 
            className="h-[200px] sm:h-[300px] lg:h-[400px] w-auto mx-auto"
          />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-extrabold text-navy-900 mb-1 leading-tight"
          style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}
        >
          We Make Trash Cans<br />
          <span className="text-blue-600 relative inline-block">
            Sparkle Clean!
            {/* Animated sparkles */}
            <motion.span
              animate={{
                scale: [0, 1.3, 0],
                rotate: [0, 180, 360],
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 1.8,
                repeat: Infinity,
                repeatDelay: 0.3,
                ease: "easeInOut"
              }}
              className="absolute -top-4 sm:-top-6 -right-6 sm:-right-10 text-3xl sm:text-5xl"
              style={{
                display: 'inline-block',
                width: '30px',
                height: '30px'
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <path d="M50 0 L55 45 L100 50 L55 55 L50 100 L45 55 L0 50 L45 45 Z" fill="#D1D5DB" />
              </svg>
            </motion.span>
            <motion.span
              animate={{
                scale: [0, 1.1, 0],
                rotate: [0, -180, -360],
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 1.6,
                repeat: Infinity,
                delay: 0.4,
                repeatDelay: 0.3,
                ease: "easeInOut"
              }}
              className="absolute -top-1 sm:-top-2 -left-10 sm:-left-14 text-2xl sm:text-4xl"
              style={{
                display: 'inline-block',
                width: '24px',
                height: '24px'
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <path d="M50 10 L54 46 L90 50 L54 54 L50 90 L46 54 L10 50 L46 46 Z" fill="#FFFFFF" />
              </svg>
            </motion.span>
            <motion.span
              animate={{
                scale: [0, 1.2, 0],
                rotate: [0, 180, 360],
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: 0.8,
                repeatDelay: 0.3,
                ease: "easeInOut"
              }}
              className="absolute -bottom-6 sm:-bottom-8 left-1/4 text-2xl sm:text-4xl"
              style={{
                display: 'inline-block',
                width: '28px',
                height: '28px'
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <path d="M50 10 L54 46 L90 50 L54 54 L50 90 L46 54 L10 50 L46 46 Z" fill="#E5E7EB" />
              </svg>
            </motion.span>
            <motion.span
              animate={{
                scale: [0, 1.4, 0],
                rotate: [0, 180, 360],
                opacity: [0, 0.9, 0]
              }}
              transition={{
                duration: 1.9,
                repeat: Infinity,
                delay: 1,
                repeatDelay: 0.3,
                ease: "easeInOut"
              }}
              className="absolute -top-6 sm:-top-8 left-6 sm:left-8 text-3xl sm:text-5xl"
              style={{
                display: 'inline-block',
                width: '32px',
                height: '32px'
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <path d="M50 0 L55 45 L100 50 L55 55 L50 100 L45 55 L0 50 L45 45 Z" fill="#9CA3AF" />
              </svg>
            </motion.span>
            <motion.span
              animate={{
                scale: [0, 1.15, 0],
                rotate: [0, -180, -360],
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: 0.6,
                repeatDelay: 0.3,
                ease: "easeInOut"
              }}
              className="absolute bottom-0 -right-4 sm:-right-6 text-2xl sm:text-4xl"
              style={{
                display: 'inline-block',
                width: '26px',
                height: '26px'
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <path d="M50 10 L54 46 L90 50 L54 54 L50 90 L46 54 L10 50 L46 46 Z" fill="#FFFFFF" />
              </svg>
            </motion.span>
            
            {/* Realistic rainbow iridescent bubbles */}
            <motion.div
              animate={{
                y: [0, -60, -120],
                x: [0, 8, 15],
                scale: [0.8, 1.1, 0],
                opacity: [0, 0.9, 0]
              }}
              transition={{
                duration: 3.5,
                repeat: Infinity,
                delay: 0.2,
                ease: "easeOut"
              }}
              className="absolute -top-4 right-8 w-8 sm:w-12 h-8 sm:h-12 rounded-full"
              style={{
                background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.8), rgba(255,182,193,0.4) 20%, rgba(173,216,230,0.4) 40%, rgba(255,255,224,0.3) 60%, rgba(221,160,221,0.3) 80%, transparent)',
                boxShadow: 'inset -2px -2px 8px rgba(255,255,255,0.6), inset 2px 2px 8px rgba(0,0,0,0.1), 0 0 15px rgba(255,255,255,0.4)'
              }}
            ></motion.div>
            
            <motion.div
              animate={{
                y: [0, -70, -140],
                x: [0, -10, -18],
                scale: [0.6, 1.3, 0],
                opacity: [0, 0.85, 0]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: 0.8,
                ease: "easeOut"
              }}
              className="absolute top-4 -left-6 sm:-left-8 w-7 sm:w-10 h-7 sm:h-10 rounded-full"
              style={{
                background: 'radial-gradient(circle at 35% 35%, rgba(255,255,255,0.9), rgba(255,218,185,0.5) 15%, rgba(176,224,230,0.5) 35%, rgba(255,240,245,0.4) 55%, rgba(230,230,250,0.4) 75%, transparent)',
                boxShadow: 'inset -2px -2px 6px rgba(255,255,255,0.7), inset 2px 2px 6px rgba(0,0,0,0.08), 0 0 12px rgba(255,255,255,0.5)'
              }}
            ></motion.div>
            
            <motion.div
              animate={{
                y: [0, -55, -110],
                x: [0, 5, 12],
                scale: [0.9, 1.2, 0],
                opacity: [0, 0.95, 0]
              }}
              transition={{
                duration: 3.2,
                repeat: Infinity,
                delay: 1.2,
                ease: "easeOut"
              }}
              className="absolute -bottom-6 left-12 w-10 sm:w-14 h-10 sm:h-14 rounded-full"
              style={{
                background: 'radial-gradient(circle at 28% 28%, rgba(255,255,255,0.85), rgba(255,192,203,0.45) 18%, rgba(135,206,250,0.45) 38%, rgba(255,255,200,0.35) 58%, rgba(216,191,216,0.35) 78%, transparent)',
                boxShadow: 'inset -3px -3px 10px rgba(255,255,255,0.6), inset 3px 3px 10px rgba(0,0,0,0.1), 0 0 18px rgba(255,255,255,0.4)'
              }}
            ></motion.div>
            
            <motion.div
              animate={{
                y: [0, -65, -130],
                x: [0, -7, -15],
                scale: [0.7, 1.15, 0],
                opacity: [0, 0.88, 0]
              }}
              transition={{
                duration: 3.8,
                repeat: Infinity,
                delay: 0.5,
                ease: "easeOut"
              }}
              className="absolute bottom-2 -right-8 sm:-right-12 w-8 sm:w-11 h-8 sm:h-11 rounded-full"
              style={{
                background: 'radial-gradient(circle at 32% 32%, rgba(255,255,255,0.88), rgba(255,228,225,0.48) 17%, rgba(176,196,222,0.48) 37%, rgba(255,250,240,0.37) 57%, rgba(221,160,221,0.37) 77%, transparent)',
                boxShadow: 'inset -2px -2px 7px rgba(255,255,255,0.65), inset 2px 2px 7px rgba(0,0,0,0.09), 0 0 14px rgba(255,255,255,0.45)'
              }}
            ></motion.div>
            
            <motion.div
              animate={{
                y: [0, -75, -150],
                x: [0, 12, 20],
                scale: [0.5, 1.4, 0],
                opacity: [0, 0.8, 0]
              }}
              transition={{
                duration: 4.2,
                repeat: Infinity,
                delay: 1.5,
                ease: "easeOut"
              }}
              className="absolute -top-10 left-1/3 w-6 sm:w-9 h-6 sm:h-9 rounded-full"
              style={{
                background: 'radial-gradient(circle at 33% 33%, rgba(255,255,255,0.9), rgba(255,182,193,0.5) 16%, rgba(173,216,230,0.5) 36%, rgba(255,255,224,0.4) 56%, rgba(230,230,250,0.4) 76%, transparent)',
                boxShadow: 'inset -2px -2px 6px rgba(255,255,255,0.7), inset 2px 2px 6px rgba(0,0,0,0.08), 0 0 12px rgba(255,255,255,0.5)'
              }}
            ></motion.div>
            
            <motion.div
              animate={{
                y: [0, -58, -116],
                x: [0, -9, -16],
                scale: [0.8, 1.25, 0],
                opacity: [0, 0.92, 0]
              }}
              transition={{
                duration: 3.6,
                repeat: Infinity,
                delay: 0.9,
                ease: "easeOut"
              }}
              className="absolute top-8 right-12 sm:right-20 w-9 sm:w-13 h-9 sm:h-13 rounded-full"
              style={{
                background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.87), rgba(255,218,185,0.47) 19%, rgba(135,206,235,0.47) 39%, rgba(255,250,240,0.37) 59%, rgba(216,191,216,0.37) 79%, transparent)',
                boxShadow: 'inset -3px -3px 9px rgba(255,255,255,0.6), inset 3px 3px 9px rgba(0,0,0,0.1), 0 0 16px rgba(255,255,255,0.4)'
              }}
            ></motion.div>
            
            <motion.div
              animate={{
                y: [0, -68, -136],
                x: [0, 6, 14],
                scale: [0.6, 1.35, 0],
                opacity: [0, 0.83, 0]
              }}
              transition={{
                duration: 4.1,
                repeat: Infinity,
                delay: 1.8,
                ease: "easeOut"
              }}
              className="absolute -bottom-8 right-4 w-7 sm:w-10 h-7 sm:h-10 rounded-full"
              style={{
                background: 'radial-gradient(circle at 31% 31%, rgba(255,255,255,0.89), rgba(255,192,203,0.49) 18%, rgba(176,224,230,0.49) 38%, rgba(255,255,200,0.39) 58%, rgba(221,160,221,0.39) 78%, transparent)',
                boxShadow: 'inset -2px -2px 7px rgba(255,255,255,0.65), inset 2px 2px 7px rgba(0,0,0,0.09), 0 0 14px rgba(255,255,255,0.45)'
              }}
            ></motion.div>
            
            <motion.div
              animate={{
                y: [0, -62, -124],
                x: [0, -11, -19],
                scale: [0.75, 1.18, 0],
                opacity: [0, 0.87, 0]
              }}
              transition={{
                duration: 3.7,
                repeat: Infinity,
                delay: 0.3,
                ease: "easeOut"
              }}
              className="absolute top-12 -left-8 sm:-left-10 w-8 sm:w-11 h-8 sm:h-11 rounded-full"
              style={{
                background: 'radial-gradient(circle at 29% 29%, rgba(255,255,255,0.86), rgba(255,228,225,0.46) 17%, rgba(173,216,230,0.46) 37%, rgba(255,250,205,0.36) 57%, rgba(230,230,250,0.36) 77%, transparent)',
                boxShadow: 'inset -2px -2px 7px rgba(255,255,255,0.65), inset 2px 2px 7px rgba(0,0,0,0.09), 0 0 14px rgba(255,255,255,0.45)'
              }}
            ></motion.div>
            
            <motion.div
              animate={{
                y: [0, -72, -144],
                x: [0, 10, 18],
                scale: [0.65, 1.28, 0],
                opacity: [0, 0.9, 0]
              }}
              transition={{
                duration: 3.9,
                repeat: Infinity,
                delay: 2.1,
                ease: "easeOut"
              }}
              className="absolute -bottom-10 left-20 w-8 sm:w-12 h-8 sm:h-12 rounded-full"
              style={{
                background: 'radial-gradient(circle at 32% 32%, rgba(255,255,255,0.88), rgba(255,182,193,0.48) 18%, rgba(135,206,250,0.48) 38%, rgba(255,255,224,0.38) 58%, rgba(216,191,216,0.38) 78%, transparent)',
                boxShadow: 'inset -3px -3px 9px rgba(255,255,255,0.6), inset 3px 3px 9px rgba(0,0,0,0.1), 0 0 16px rgba(255,255,255,0.4)'
              }}
            ></motion.div>
            
            <motion.div
              animate={{
                y: [0, -64, -128],
                x: [0, -8, -17],
                scale: [0.7, 1.22, 0],
                opacity: [0, 0.86, 0]
              }}
              transition={{
                duration: 3.5,
                repeat: Infinity,
                delay: 1.0,
                ease: "easeOut"
              }}
              className="absolute top-6 right-6 w-7 sm:w-10 h-7 sm:h-10 rounded-full"
              style={{
                background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.87), rgba(255,218,185,0.47) 17%, rgba(176,196,222,0.47) 37%, rgba(255,250,240,0.37) 57%, rgba(221,160,221,0.37) 77%, transparent)',
                boxShadow: 'inset -2px -2px 7px rgba(255,255,255,0.65), inset 2px 2px 7px rgba(0,0,0,0.09), 0 0 14px rgba(255,255,255,0.45)'
              }}
            ></motion.div>
            
            <motion.div
              animate={{
                y: [0, -70, -140],
                x: [0, 7, 15],
                scale: [0.55, 1.32, 0],
                opacity: [0, 0.84, 0]
              }}
              transition={{
                duration: 4.0,
                repeat: Infinity,
                delay: 0.6,
                ease: "easeOut"
              }}
              className="absolute -bottom-12 -right-6 sm:-right-8 w-8 sm:w-11 h-8 sm:h-11 rounded-full"
              style={{
                background: 'radial-gradient(circle at 31% 31%, rgba(255,255,255,0.88), rgba(255,192,203,0.48) 18%, rgba(173,216,230,0.48) 38%, rgba(255,255,200,0.38) 58%, rgba(230,230,250,0.38) 78%, transparent)',
                boxShadow: 'inset -2px -2px 7px rgba(255,255,255,0.65), inset 2px 2px 7px rgba(0,0,0,0.09), 0 0 14px rgba(255,255,255,0.45)'
              }}
            ></motion.div>
            
            <motion.div
              animate={{
                y: [0, -66, -132],
                x: [0, -6, -13],
                scale: [0.85, 1.16, 0],
                opacity: [0, 0.91, 0]
              }}
              transition={{
                duration: 3.4,
                repeat: Infinity,
                delay: 1.4,
                ease: "easeOut"
              }}
              className="absolute top-14 left-6 w-8 sm:w-12 h-8 sm:h-12 rounded-full"
              style={{
                background: 'radial-gradient(circle at 33% 33%, rgba(255,255,255,0.86), rgba(255,228,225,0.46) 19%, rgba(135,206,235,0.46) 39%, rgba(255,250,205,0.36) 59%, rgba(216,191,216,0.36) 79%, transparent)',
                boxShadow: 'inset -3px -3px 9px rgba(255,255,255,0.6), inset 3px 3px 9px rgba(0,0,0,0.1), 0 0 16px rgba(255,255,255,0.4)'
              }}
            ></motion.div>
            
            {/* More sparkles */}
            <motion.span
              animate={{
                scale: [0, 1, 0],
                rotate: [0, -180, -360],
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 1.7,
                repeat: Infinity,
                delay: 0.2,
                repeatDelay: 0.3,
                ease: "easeInOut"
              }}
              className="absolute top-2 right-4 text-2xl sm:text-3xl"
              style={{
                display: 'inline-block',
                width: '22px',
                height: '22px'
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <path d="M50 10 L54 46 L90 50 L54 54 L50 90 L46 54 L10 50 L46 46 Z" fill="#FFFFFF" />
              </svg>
            </motion.span>
            <motion.span
              animate={{
                scale: [0, 1.25, 0],
                rotate: [0, 180, 360],
                opacity: [0, 0.95, 0]
              }}
              transition={{
                duration: 1.8,
                repeat: Infinity,
                delay: 1.2,
                repeatDelay: 0.3,
                ease: "easeInOut"
              }}
              className="absolute -bottom-4 -left-6 sm:-left-8 text-2xl sm:text-4xl"
              style={{
                display: 'inline-block',
                width: '28px',
                height: '28px'
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <path d="M50 0 L55 45 L100 50 L55 55 L50 100 L45 55 L0 50 L45 45 Z" fill="#D1D5DB" />
              </svg>
            </motion.span>
            <motion.span
              animate={{
                scale: [0, 1.3, 0],
                rotate: [0, 180, 360],
                opacity: [0, 0.9, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: 0.9,
                repeatDelay: 0.3,
                ease: "easeInOut"
              }}
              className="absolute bottom-4 right-10 sm:right-16 text-3xl sm:text-5xl"
              style={{
                display: 'inline-block',
                width: '32px',
                height: '32px'
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <path d="M50 10 L54 46 L90 50 L54 54 L50 90 L46 54 L10 50 L46 46 Z" fill="#E5E7EB" />
              </svg>
            </motion.span>
          </span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="inline-block mb-2"
        >
          <div className="bg-white/90 backdrop-blur-sm text-gray-800 px-4 sm:px-8 py-2 sm:py-3 rounded-full shadow-xl border-2 border-white/50">
            <p className="text-base sm:text-xl font-bold" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
              Kid-Powered Cleaning Service
            </p>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="text-base sm:text-xl md:text-2xl text-gray-700 mb-4 sm:mb-6 max-w-3xl mx-auto leading-relaxed px-4"
        >
          Professional trash can cleaning for your neighborhood, brought to you by kids! Choose one-time service or save with our subscription plans.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-8 sm:mb-10 px-4"
        >
          <button
            onClick={handleGetStartedClick}
            className="w-full sm:w-auto bg-white border-4 border-red-500 text-red-500 px-8 sm:px-12 py-4 sm:py-5 rounded-full text-lg sm:text-xl font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
          >
            Get Started
          </button>
          <button
            onClick={() => scrollToSection('pricing')}
            className="w-full sm:w-auto bg-white border-4 border-yellow-400 text-red-600 px-8 sm:px-12 py-4 sm:py-5 rounded-full text-lg sm:text-xl font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
          >
            See Pricing
          </button>
        </motion.div>

        {/* Feature badges */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="grid grid-cols-2 sm:grid-cols-3 md:flex md:items-center md:justify-center gap-4 sm:gap-6 px-4"
        >
          {[
            { icon: 'ri-sparkling-2-fill', text: 'Sparkling Clean', color: 'text-yellow-500', borderColor: 'border-yellow-500' },
            { icon: 'ri-calendar-check-fill', text: 'Flexible Plans', color: 'text-blue-600', borderColor: 'border-blue-600' },
            { icon: 'ri-money-dollar-circle-fill', text: 'Great Prices', color: 'text-green-500', borderColor: 'border-green-500' },
            { icon: 'ri-emotion-happy-fill', text: 'Happy Service', color: 'text-red-500', borderColor: 'border-red-500' },
            { icon: 'ri-home-smile-fill', text: 'Neighborhood Care', color: 'text-purple-500', borderColor: 'border-purple-500' }
          ].map((badge, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.1, rotate: 5 }}
              className={`bg-white border-4 ${badge.borderColor} p-4 sm:p-6 rounded-3xl shadow-lg w-full sm:w-32 h-28 sm:h-32 flex flex-col items-center justify-center cursor-pointer ${index === 4 ? 'col-span-2 sm:col-span-1' : ''}`}
            >
              <i className={`${badge.icon} text-3xl sm:text-4xl ${badge.color} mb-2`}></i>
              <p className="text-xs sm:text-sm font-semibold text-gray-800 text-center leading-tight" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>{badge.text}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}